<script lang="ts" setup>
import type { Swiper as SwiperClass } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';

import type { ProductType } from '~/utils/api/common';

const props = defineProps<{
  list: string[];
}>();

const { data: commonData } = await useAsyncCommonData();
const { t } = useI18n();

const products = computed(
  () =>
    props.list
      .map((id) => commonData.value.products.find((el) => el.code === id && !el.secret))
      .filter(Boolean) as ProductType[],
);

// Карусель только для блока из 3 товаров: на мобильном видно 2 крупные карточки
// + peek третьей, на десктопе — 3 в ряд. Блоки из 1/2/4 товаров не меняются.
const isSlider = computed(() => products.value.length === 3);

// Прямой Swiper (паттерн productCartSamplers) — даёт внешний контроль навигацией,
// чтобы рендерить компактные chevron-стрелочки рядом со слайдером.
const swiperInstance = ref<SwiperClass>();
const isStart = ref(false);
const isEnd = ref(false);

const setSwiper = (swiper: SwiperClass) => {
  swiperInstance.value = swiper;
  isStart.value = swiper.isBeginning;
  isEnd.value = swiper.isEnd;
};

const onSlideChange = (swiper: SwiperClass) => {
  isStart.value = swiper.isBeginning;
  isEnd.value = swiper.isEnd;
};

// mobile: 2.2 слайда (2 полных + peek третьего), от 768px — 3 в ряд.
const breakpoints = {
  0: { slidesPerView: 2.2, spaceBetween: 10 },
  768: { slidesPerView: 3, spaceBetween: 18 },
};

onBeforeUnmount(() => {
  swiperInstance.value?.destroy();
});
</script>

<template>
  <div class="contentCatalog" :class="[`contentCatalog--${products.length}`, { 'contentCatalog--slider': isSlider }]">
    <div v-if="isSlider" class="contentCatalog__sliderWrap">
      <div class="contentCatalog__nav">
        <button
          type="button"
          class="contentCatalog__button"
          :disabled="isStart"
          :aria-label="t('prev')"
          @click="swiperInstance?.slidePrev()"
        >
          <SvgoSimpleChevronLeft />
        </button>
        <button
          type="button"
          class="contentCatalog__button"
          :disabled="isEnd"
          :aria-label="t('next')"
          @click="swiperInstance?.slideNext()"
        >
          <SvgoSimpleChevronRight />
        </button>
      </div>

      <Swiper
        class="contentCatalog__slider"
        :breakpoints="breakpoints"
        @swiper="setSwiper"
        @slide-change-transition-end="onSlideChange"
        @reach-beginning="onSlideChange"
        @reach-end="onSlideChange"
      >
        <SwiperSlide v-for="item in products" :key="item.id" class="contentCatalog__slide">
          <ProductCard :product="item" new-tab />
        </SwiperSlide>
      </Swiper>
    </div>

    <ProductCard v-for="item in products" v-else :key="item.id" :product="item" new-tab />
  </div>
</template>

<style src="swiper/css" />
<style lang="scss">
.contentCatalog {
  display: grid;
  column-gap: 18px;
  row-gap: 40px;
  position: relative;
  margin-bottom: 40px;

  // The backend flags some of these products isNew, so a "Новинка" badge
  // shows on their cards — but they aren't new. Hide the new badge in article
  // catalogs (sale/subscription badges stay).
  .productCard__badge_new {
    display: none;
  }

  &--3 {
    grid-template-columns: repeat(3, 1fr);

    // At 3-up the cards are narrow; widen the cart button to the card
    // edges and tighten its side padding so the label stays on one line.
    // Drop the label one step (16px -> 15px) so it doesn't crowd the edges.
    .productCard__actions {
      padding-left: 2px;
      padding-right: 2px;
    }

    .productCardActions__button_main {
      padding: 0 6px;
      font-size: 15px;
    }

    .productCardActions__text_state {
      white-space: nowrap;
    }
  }

  &--1,
  &--2,
  &--4 {
    grid-template-columns: repeat(2, 1fr);
  }

  // 3-product block becomes a Swiper carousel instead of a 3-col grid.
  &--slider {
    display: block;
  }

  // Badges sit on the card's top edge, half above it (translateY(-50%)).
  // Swiper's own overflow:hidden crops that overhang — let badges show and
  // clip only the horizontal overflow (the peek of the next slide) on the wrap.
  &__sliderWrap {
    overflow-x: clip;
  }

  &__slider {
    overflow: visible;
  }

  // Compact chevron navigation, top-right above the slider (mobile only).
  // Pattern reused from productCartSamplers.
  &__nav {
    display: none;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 12px;

    @include mq('sm') {
      display: flex;
    }
  }

  &__button {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #121212;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: opacity 0.3s ease;

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    svg {
      width: 6px;
      height: 11px;
      display: block;
    }
  }

  &__slide {
    height: auto;

    .productCard {
      height: 100%;
    }
  }

  @include mq('sm') {
    column-gap: 10px;
    row-gap: 44px;
  }
}
</style>

<i18n>
ru:
  prev: Предыдущий слайд
  next: Следующий слайд

en:
  prev: Previous slide
  next: Next slide
</i18n>
